import { supabase } from '../lib/supabase';

export const companyService = {
  /**
   * Get all companies (admin only)
   */
  async getAll() {
    const { data, error } = await supabase?.from('companies')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(company => ({
      id: company?.id,
      name: company?.name,
      slug: company?.slug,
      description: company?.description,
      logo: company?.logo,
      primaryColor: company?.primary_color,
      secondaryColor: company?.secondary_color,
      email: company?.email,
      phone: company?.phone,
      website: company?.website,
      address: company?.address,
      ctaText: company?.cta_text,
      ctaUrl: company?.cta_url,
      isActive: company?.is_active,
      createdBy: company?.created_by,
      createdAt: company?.created_at,
      updatedAt: company?.updated_at
    })) || [];
  },

  /**
   * Get a single company by ID
   */
  async getById(id) {
    const { data, error } = await supabase?.from('companies')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      description: data?.description,
      logo: data?.logo,
      primaryColor: data?.primary_color,
      secondaryColor: data?.secondary_color,
      email: data?.email,
      phone: data?.phone,
      website: data?.website,
      address: data?.address,
      ctaText: data?.cta_text,
      ctaUrl: data?.cta_url,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Get a single company by slug
   */
  async getBySlug(slug) {
    try {
      // Normalize slug: decode URI component and convert to lowercase
      const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

      if (!normalizedSlug) {
        console.warn('Empty slug provided to getBySlug');
        return null;
      }

      console.log('Fetching company with normalized slug:', normalizedSlug);

      const { data, error } = await supabase
        ?.from('companies')
        ?.select('*')
        ?.eq('slug', normalizedSlug)
        ?.eq('is_active', true)
        ?.single();

      if (error) {
        // PGRST116 = no rows returned
        if (error?.code === 'PGRST116') {
          console.warn(`Company not found with slug: ${normalizedSlug}`);
          return null;
        }
        // Log other errors but don't throw - return null for better UX
        console.error('Error fetching company by slug:', error);
        return null;
      }

      if (!data) {
        console.warn(`No company data returned for slug: ${normalizedSlug}`);
        return null;
      }

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        name: data?.name,
        slug: data?.slug,
        description: data?.description,
        logo: data?.logo,
        primaryColor: data?.primary_color,
        secondaryColor: data?.secondary_color,
        email: data?.email,
        phone: data?.phone,
        website: data?.website,
        address: data?.address,
        ctaText: data?.cta_text,
        ctaUrl: data?.cta_url,
        isActive: data?.is_active,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      // Catch any unexpected errors
      console.error('Unexpected error in getBySlug:', err);
      return null;
    }
  },

  /**
   * Create a new company
   */
  async create(company) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case for DB
    const { data, error } = await supabase?.from('companies')?.insert({
        name: company?.name,
        slug: company?.slug,
        description: company?.description,
        logo: company?.logo,
        primary_color: company?.primaryColor,
        secondary_color: company?.secondaryColor,
        email: company?.email,
        phone: company?.phone,
        website: company?.website,
        address: company?.address,
        cta_text: company?.ctaText,
        cta_url: company?.ctaUrl,
        is_active: company?.isActive,
        created_by: user?.id
      })?.select()?.single();

    if (error) throw error;

    // Convert back to camelCase
    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      description: data?.description,
      logo: data?.logo,
      primaryColor: data?.primary_color,
      secondaryColor: data?.secondary_color,
      email: data?.email,
      phone: data?.phone,
      website: data?.website,
      address: data?.address,
      ctaText: data?.cta_text,
      ctaUrl: data?.cta_url,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update an existing company
   */
  async update(id, company) {
    // Convert camelCase to snake_case for DB
    const { data, error } = await supabase?.from('companies')?.update({
        name: company?.name,
        slug: company?.slug,
        description: company?.description,
        logo: company?.logo,
        primary_color: company?.primaryColor,
        secondary_color: company?.secondaryColor,
        email: company?.email,
        phone: company?.phone,
        website: company?.website,
        address: company?.address,
        cta_text: company?.ctaText,
        cta_url: company?.ctaUrl,
        is_active: company?.isActive
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    // Convert back to camelCase
    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      description: data?.description,
      logo: data?.logo,
      primaryColor: data?.primary_color,
      secondaryColor: data?.secondary_color,
      email: data?.email,
      phone: data?.phone,
      website: data?.website,
      address: data?.address,
      ctaText: data?.cta_text,
      ctaUrl: data?.cta_url,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete a company
   */
  async delete(id) {
    const { error } = await supabase?.from('companies')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  /**
   * Toggle company active status
   */
  async toggleStatus(id, isActive) {
    const { data, error } = await supabase?.from('companies')?.update({ is_active: isActive })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isActive: data?.is_active
    };
  }
};